import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';

// Mock data for savings accounts and goals
const mockSavingsAccounts = [
  {
    id: 1,
    name: 'High-Yield Savings',
    balance: 12500,
    apy: 4.25,
    type: 'savings',
    lastUpdated: '2025-06-13T14:30:00Z'
  },
  {
    id: 2,
    name: 'Emergency Fund',
    balance: 8500,
    apy: 3.75,
    type: 'savings',
    lastUpdated: '2025-06-13T14:30:00Z'
  }
];

const mockSavingsGoals = [
  {
    id: 1,
    name: 'Emergency Fund',
    target: 15000,
    current: 8500,
    deadline: '2025-12-31',
    monthlyContribution: 1000
  },
  {
    id: 2,
    name: 'Vacation Fund',
    target: 5000,
    current: 1200,
    deadline: '2025-07-15',
    monthlyContribution: 500
  },
  {
    id: 3,
    name: 'New Laptop',
    target: 2000,
    current: 500,
    deadline: '2025-08-30',
    monthlyContribution: 300
  }
];

const Savings = () => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [showNewGoalForm, setShowNewGoalForm] = useState(false);
  const [newGoal, setNewGoal] = useState({
    name: '',
    target: '',
    deadline: '',
    monthlyContribution: ''
  });

  // Calculate total savings
  const totalSavings = mockSavingsAccounts.reduce((sum, account) => sum + account.balance, 0);
  
  // Calculate total monthly income (placeholder)
  const monthlyIncome = 6500;
  const monthlySavings = mockSavingsGoals.reduce((sum, goal) => sum + goal.monthlyContribution, 0);
  const savingsRate = Math.round((monthlySavings / monthlyIncome) * 100);

  // Calculate days until deadline
  const daysUntilDeadline = (deadline) => {
    const oneDay = 24 * 60 * 60 * 1000;
    const deadlineDate = new Date(deadline);
    const today = new Date();
    return Math.ceil((deadlineDate - today) / oneDay);
  };

  // Calculate progress percentage
  const calculateProgress = (current, target) => {
    return Math.min(Math.round((current / target) * 100), 100);
  };

  const handleNewGoalChange = (e) => {
    const { name, value } = e.target;
    setNewGoal(prev => ({
      ...prev,
      [name]: name === 'name' ? value : parseFloat(value) || ''
    }));
  };

  const handleAddGoal = (e) => {
    e.preventDefault();
    // In a real app, this would add to your backend
    console.log('New goal:', newGoal);
    setShowNewGoalForm(false);
    setNewGoal({
      name: '',
      target: '',
      deadline: '',
      monthlyContribution: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Savings</h1>
          <p className="mt-1 text-gray-600">Track your savings progress and set financial goals</p>
        </div>
        <button
          onClick={() => setShowNewGoalForm(true)}
          className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Savings Goal
        </button>
      </div>

      {/* Savings Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-medium text-gray-900">Total Savings</h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">{formatCurrency(totalSavings)}</p>
          <p className="mt-1 text-sm text-gray-500">Across all savings accounts</p>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-medium text-gray-900">Monthly Savings Rate</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">{savingsRate}%</p>
          <p className="mt-1 text-sm text-gray-500">{formatCurrency(monthlySavings)} saved per month</p>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-medium text-gray-900">Active Goals</h3>
          <p className="mt-2 text-3xl font-bold text-purple-600">{mockSavingsGoals.length}</p>
          <p className="mt-1 text-sm text-gray-500">Savings goals in progress</p>
        </div>
      </div>

      {/* Goals Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Your Savings Goals</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                activeTab === 'overview' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('challenges')}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                activeTab === 'challenges' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Challenges
            </button>
          </div>
        </div>

        {activeTab === 'overview' ? (
          <div className="space-y-6">
            {mockSavingsGoals.map((goal) => {
              const progress = calculateProgress(goal.current, goal.target);
              const daysLeft = daysUntilDeadline(goal.deadline);
              const monthlyNeeded = (goal.target - goal.current) / (daysLeft / 30);
              
              return (
                <div key={goal.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{goal.name}</h3>
                      <p className="text-sm text-gray-500">
                        {formatCurrency(goal.current)} of {formatCurrency(goal.target)} saved
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {daysLeft > 0 ? `${daysLeft} days left` : 'Past deadline'}
                    </span>
                  </div>
                  
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-gray-500">
                      <span>{progress}% Complete</span>
                      <span>{formatCurrency(goal.target - goal.current)} to go</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-sm">
                    <p className="text-gray-600">
                      <span className="font-medium">Monthly contribution:</span> {formatCurrency(goal.monthlyContribution)}
                    </p>
                    {daysLeft > 0 && (
                      <p className="text-gray-600">
                        <span className="font-medium">To reach goal on time:</span> {formatCurrency(monthlyNeeded)}/month needed
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
            
            {showNewGoalForm && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">New Savings Goal</h3>
                <form onSubmit={handleAddGoal} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Goal Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={newGoal.name}
                      onChange={handleNewGoalChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="target" className="block text-sm font-medium text-gray-700">Target Amount ($)</label>
                      <input
                        type="number"
                        id="target"
                        name="target"
                        value={newGoal.target}
                        onChange={handleNewGoalChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        min="1"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">Target Date</label>
                      <input
                        type="date"
                        id="deadline"
                        name="deadline"
                        value={newGoal.deadline}
                        onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="monthlyContribution" className="block text-sm font-medium text-gray-700">Monthly Contribution ($)</label>
                      <input
                        type="number"
                        id="monthlyContribution"
                        name="monthlyContribution"
                        value={newGoal.monthlyContribution}
                        onChange={handleNewGoalChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        min="1"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowNewGoalForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Create Goal
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">52-Week Savings Challenge</h3>
              <p className="text-gray-600 mb-4">Save a little more each week to build your savings habit.</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '35%' }}></div>
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>Week 18</span>
                <span>${(18 * 18 + 18) / 2} saved of $1,378</span>
              </div>
              <button className="mt-4 text-sm text-blue-600 hover:text-blue-800 font-medium">
                View Challenge Details →
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No-Spend Challenge</h3>
              <p className="text-gray-600 mb-4">Challenge yourself to no-spend days to boost your savings.</p>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                  <div key={day} className="flex-1 text-center">
                    <div className={`h-8 rounded flex items-center justify-center text-xs font-medium ${
                      day < new Date().getDay() 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'][day]}
                    </div>
                    <div className={`mt-1 h-8 rounded flex items-center justify-center text-xs ${
                      day < new Date().getDay() 
                        ? 'bg-green-50 text-green-700 font-medium' 
                        : 'bg-gray-50 text-gray-400'
                    }`}>
                      {day < new Date().getDay() ? '✓' : day === new Date().getDay() ? 'Today' : ''}
                    </div>
                  </div>
                ))}
              </div>
              <button className="mt-4 text-sm text-blue-600 hover:text-blue-800 font-medium">
                Start Challenge →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* High-Yield Accounts Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">High-Yield Savings Opportunities</h2>
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            View All →
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: 'Wealthfront', apy: 4.55, minDeposit: 1, features: ['No fees', 'FDIC insured', 'Mobile app'] },
            { name: 'Ally Bank', apy: 4.25, minDeposit: 0, features: ['No fees', '24/7 customer service', 'ATM access'] },
            { name: 'Marcus by Goldman Sachs', apy: 4.4, minDeposit: 0, features: ['No fees', 'No minimums', 'Mobile check deposit'] },
          ].map((account, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{account.name}</h3>
                  <p className="text-2xl font-bold text-green-600">{account.apy}% APY</p>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Featured
                </span>
              </div>
              <ul className="mt-4 space-y-2">
                {account.features.map((feature, i) => (
                  <li key={i} className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              <button className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors">
                Open Account
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Savings;
